import { Switch, SwitchThumb } from '@radix-ui/react-switch';
import classNames from 'classnames';
import React from 'react';
import type { Recommendation } from '../../types';
import * as styles from '../Recommendations.css';
import type { RecommendationCardSchema, TRecommendationAttributes } from '../Recommendations.types';
import Description from './Description';
import Image from './Image';
import Name from './Name';
import Sponsored from './Sponsored';

const cardSchema: RecommendationCardSchema = {
  node: 'div',
  children: [
    {
      node: 'image',
      children: [],
      className: 'object-cover h-16 w-16 rounded-lg',
    },
    {
      node: 'div',
      className: 'flex w-full justify-between gap-2',
      children: [
        {
          node: 'div',
          className: 'flex flex-col gap-1',
          children: [
            {
              node: 'sponsored',
              className: 'flex flex-col text-left',
              children: [],
            },
            {
              node: 'div',
              className: 'flex flex-col',
              children: [
                {
                  node: 'name',
                  children: [],
                  className: 'flex flex-col text-left',
                },
                {
                  node: 'description',
                  children: [],
                  className: 'flex flex-col text-left',
                },
              ],
            },
          ],
        },
        {
          node: 'checkbox',
          children: [],
          className: '',
        },
      ],
    },
  ],
  className: 'flex flex-row gap-4',
};

interface SerializerProps {
  isChecked: boolean;
  recommendation: Recommendation;
  attributes: TRecommendationAttributes;
  onChange: (recommendation: Recommendation) => void;
}

const Serializer: React.FC<SerializerProps> = ({ isChecked, recommendation, onChange }) => {
  const renderNode = (node: RecommendationCardSchema): React.ReactNode => {
    switch (node.node) {
      case 'div':
        return (
          <div className={classNames(node.className)}>
            {node.children.map((child, index) => (
              <React.Fragment key={index}>{renderNode(child)}</React.Fragment>
            ))}
          </div>
        );
      case 'image':
        return (
          <Image
            className={classNames(node.className, 'rounded-lg shrink-0')}
            url={recommendation?.logo_url}
            alt={recommendation?.name}
          />
        );
      case 'name':
        return <Name className={classNames(node.className, 'font-semibold text-lg')} name={recommendation?.name} />;
      case 'description':
        return (
          <Description
            className={classNames(node.className, 'text-sm')}
            description={recommendation?.description || ''}
          />
        );
      case 'checkbox':
        return (
          <div className={classNames(node.className)}>
            <Switch
              onCheckedChange={(_value: boolean) => onChange(recommendation)}
              checked={isChecked}
              value={recommendation.isOwner ? recommendation.recommended_publication_id : recommendation.id}
              className={classNames(styles.selectAllSwitchRoot)}
              style={{
                backgroundColor: isChecked
                  ? 'var(--toggle-checked-background-color)'
                  : 'var(--toggle-background-color)',
              }}
              name={recommendation.isOwner ? 'publication_ids[]' : 'recommendation_ids[]'}
            >
              <SwitchThumb className={classNames(styles.selectAllSwitchThumb)} />
            </Switch>
          </div>
        );
      case 'sponsored':
        return recommendation.sponsored ? (
          <Sponsored className={classNames(node.className, 'text-xs font-semibold')} text="Sponsored" />
        ) : null;
      default:
        return null;
    }
  };

  return <>{renderNode(cardSchema)}</>;
};

export default Serializer;
